import { useEditor, EditorContent } from '@tiptap/react';
import { Box, Paper, Group, Loader, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { richTextPresetsApi, RichTextPresetStyle } from '@/services/rich-text-presets.api';
import { buildExtensions } from './extensions';
import { applyPreset as applyPresetToEditor } from './presets';
import { EditorStyles } from './editorStyles';
import { EditorToolbar } from './EditorToolbar';
import { useImageUpload, imageFilesFrom } from '@/features/editor/hooks/useImageUpload';
import { insertUploadedImages } from './insertUploadedImages';

interface Props {
    content: string;
    onChange: (content: string) => void;
    variables?: { id: string; label: string }[];
    placeholder?: string;
    /** HTML from the rest of the flow, mined for colors to offer as swatches. */
    flowContent?: string[];
}

export default function ScriptEditor({ content, onChange, variables = [], placeholder = "Type text or '@' to insert a variable", flowContent = [] }: Props) {
    const [textColor, setTextColor] = useState('#000000');
    const [bgColor, setBgColor] = useState('#FFFF00');
    const { data: presets = [] } = useQuery({
        queryKey: ['rich-text-presets'],
        queryFn: richTextPresetsApi.getPresets,
    });

    const { uploading, uploadFlowContent } = useImageUpload();

    /**
     * Upload dropped/pasted files, then insert them at `position` — or at the
     * cursor when position is undefined (paste). Insertion happens after the
     * await, so the images land in the order they were dropped.
     */
    const insertImages = async (files: File[], position?: number) => {
        const assets = await uploadFlowContent(files);
        // Read the editor after the await: it may have been destroyed meanwhile.
        insertUploadedImages(editorRef.current, assets, position);
    };

    const editor = useEditor({
        immediatelyRender: false,
        extensions: buildExtensions({ placeholder, variables }),
        content,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'tiptap-editor',
                style: 'outline: none; min-height: 100px;',
            },
            // ProseMirror owns drop and paste, so these cannot be plain DOM
            // handlers on a wrapper — the editor would swallow them first.
            handleDrop: (view, event) => {
                const files = imageFilesFrom((event as DragEvent).dataTransfer);
                if (files.length === 0) return false; // let ProseMirror handle text/HTML drops

                event.preventDefault();
                // Resolve the drop point before awaiting: the coordinates are
                // meaningless once the event has been handled.
                const at = view.posAtCoords({
                    left: (event as DragEvent).clientX,
                    top: (event as DragEvent).clientY,
                });
                void insertImages(files, at?.pos);
                return true;
            },
            handlePaste: (_view, event) => {
                const files = imageFilesFrom((event as ClipboardEvent).clipboardData);
                if (files.length === 0) return false; // ordinary paste

                event.preventDefault();
                void insertImages(files);
                return true;
            },
        },
    });

    // handleDrop/handlePaste are captured when the editor is created, so they
    // close over a null `editor`. Read it through a ref instead.
    const editorRef = useRef(editor);
    editorRef.current = editor;

    // Update content if it changes externally
    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            // Only update if content is different to avoid cursor jumps
            // A better check would be needed for robust two-way binding
            // editor.commands.setContent(content);
        }
    }, [content, editor]);

    if (!editor) {
        return null;
    }

    const applyPreset = (style: RichTextPresetStyle) =>
        applyPresetToEditor(editor, style, { setTextColor, setBgColor });

    const textPresets = presets.filter((preset) => !preset.style.boxEnabled);
    const calloutPresets = presets.filter((preset) => preset.style.boxEnabled);

    return (
        <>
            <EditorStyles />
            <Paper withBorder radius="sm" style={{ border: '1px solid #dee2e6' }}>
                <EditorToolbar
                    editor={editor}
                    textColor={textColor}
                    bgColor={bgColor}
                    setTextColor={setTextColor}
                    setBgColor={setBgColor}
                    textPresets={textPresets}
                    calloutPresets={calloutPresets}
                    onApplyPreset={applyPreset}
                    flowContent={flowContent}
                />

                {/* Editor Content */}
                <Box p="md" style={{ minHeight: '200px', backgroundColor: '#ffffff', position: 'relative' }}>
                    <EditorContent editor={editor} />
                    {uploading && (
                        <Group
                            gap="xs"
                            align="center"
                            style={{
                                position: 'absolute',
                                bottom: 8,
                                right: 12,
                                background: 'rgba(255,255,255,0.95)',
                                borderRadius: 4,
                                padding: '4px 8px',
                            }}
                        >
                            <Loader size="xs" />
                            <Text size="xs" c="dimmed">Uploading image…</Text>
                        </Group>
                    )}
                </Box>
            </Paper>
        </>
    );
}
